/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2025 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

/**
 * <xng-change>
 *  Override thiscomponent, to change when the qty input get displayed and disabled.
 *  In PDP page, it should be always visible, but disabled as needed.
 * </xng-change>
 */

import QtyField from "@eisinc/App/Components/Qty/QtyField";
import { getValidatedQty } from "@eisinc/App/Utilities/helpers";
import { TextFieldPresentationProps, TextFieldProps } from "@eisinc/MobiusOverrides/TextField";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { canAddToCart, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import React, { ChangeEvent, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

// <xng-change>
export interface UnitOfMeasureData {
    UnitOfMeasure: string;
    MinimumOrderQuantity: string;
    MultipleSaleQuantity: string;
    isFractionalMoq?: string;
}
// </xng-change>

interface OwnProps extends TextFieldProps {
    configurationCompleted?: boolean;
    configurationSelection?: SafeDictionary<string>;
    variantSelectionCompleted?: boolean;
    labelOverride?: React.ReactNode;
    extendedStyles?: TextFieldPresentationProps;
    // Xng-Change
    alwaysVisible?: boolean;
    isQuote?: boolean;
    isQuickOrder?: boolean;
}

const mapStateToProps = (
    state: ApplicationState,
    props: {
        configurationCompleted?: boolean;
        variantSelectionCompleted?: boolean;
        configurationSelection?: SafeDictionary<string>;
    } & HasProductContext,
) => {
    return {
        canAddToCart: canAddToCart(
            state,
            props.productContext.product as ProductModel,
            props.productContext.productInfo,
            props.configurationCompleted,
            props.variantSelectionCompleted,
            props.configurationSelection,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
        // Xng-Change
        product: props.productContext.product as ProductModel,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasProductContext;

export const productQuantityOrderedStyles: TextFieldPresentationProps = {
    cssOverrides: {
        formInputWrapper: css`
            input[type="number"]::-webkit-outer-spin-button,
            input[type="number"]::-webkit-inner-spin-button {
                /* ISC-11023 deal with this. */
                /* stylelint-disable-next-line */
                -webkit-appearance: none;
                margin: 0;
            }
        `,
        inputSelect: css`
            /* ISC-11023 deal with this. */
            /* stylelint-disable-next-line */
            -moz-appearance: textfield;
        `,
    },
};

const ProductQuantityOrdered = ({
    hasEnoughInventory,
    productContext: {
        // Xng-Change
        product: { id: productId, properties: productProperties, minimumOrderQty },
        onQtyOrderedChanged,
        // Xng-Change
        productInfo: { qtyOrdered, unitOfMeasure, failedToLoadInventory },
    },
    labelOverride,
    extendedStyles,
    canAddToCart,
    configurationCompleted,
    variantSelectionCompleted,
    // Xng-Change
    product,
    alwaysVisible,
    isQuote,
    isQuickOrder,
    configurationSelection,
    ...otherProps
}: Props) => {
    const [styles] = useState(() => mergeToNew(productQuantityOrderedStyles, extendedStyles));
    // <xng-change>
    // const [productQty, setProductQty] = useState(qtyOrdered.toString());
    // useEffect(() => {
    //     if (qtyOrdered === Number(productQty)) {
    //         return;
    //     }
    //     setProductQty(`${qtyOrdered || ""}`);
    // }, [qtyOrdered]);

    // if ((!hasEnoughInventory && !failedToLoadInventory) || !canAddToCart) {
    //     return null;
    // }

    // const keyPressHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
    //     if (event.which === 13) {
    //         (event.target as any).blur();
    //     }
    // };

    const [productQty, setProductQty] = useState<string | number>(
        parseFloat(productProperties?.minimumOrderQty) || parseFloat(minimumOrderQty?.toString()) || 1,
    );
    const [initialProductQty, setInitialProductQty] = useState(
        Math.max(Number(productProperties?.minimumOrderQty), qtyOrdered),
    );
    const unitOfMeasuresData: UnitOfMeasureData[] = JSON.parse(productProperties?.unitOfMeasureData || "[]");
    const possibleMinQty = productProperties?.minimumOrderQty || null;
    const isProductFromCart = !!productProperties?.originalQuoteLineId || false;
    const qtyCameFromCartMerge = parseFloat(productProperties?.qtyCameFromCartMerge);
    const originalQuotedQty = parseFloat(productProperties?.originalQuotedQty);
    const [allowFractionalQty, setAllowFractionalQty] = useState(false);
    const regexp = allowFractionalQty ? /^[0-9]*[.,]?[0-9]*$/ : /^[0-9\b]+$/;

    const isValidNumberic = (number: string) => {
        if (number === "") {
            // backspace or delete case
            return true;
        }
        return regexp.test(number);
    };

    useEffect(() => {
        if (unitOfMeasuresData && unitOfMeasure) {
            const selectedUOM = unitOfMeasuresData.filter(uom => uom.UnitOfMeasure === unitOfMeasure);
            let minQty;
            if (selectedUOM.length) {
                minQty = parseFloat(selectedUOM[0].MinimumOrderQuantity) || 1;
            } else {
                minQty =
                    parseFloat(productProperties?.minimumOrderQty) ||
                    parseFloat(minimumOrderQty?.toString()) ||
                    qtyOrdered ||
                    1;
            }

            const isFractionalMoq = !Number.isInteger(minQty);
            setAllowFractionalQty(isFractionalMoq);

            setProductQty(minQty);

            // To fix the add to cart qty when the qtyOrdered is not equal to the minimumOrderQty
            if (onQtyOrderedChanged) {
                if (!isQuickOrder && !isQuote && !Number.isNaN(minQty)) {
                    onQtyOrderedChanged(minQty);
                }
            }
        } else {
            const value = parseFloat(productProperties?.minimumOrderQty) || 1;
            onQtyOrderedChanged?.(value);
        }
    }, [unitOfMeasure]);

    useEffect(() => {
        if (qtyOrdered === Number(productQty)) {
            return;
        }
        // search auto-complete part
        if (possibleMinQty && qtyOrdered < parseFloat(possibleMinQty)) {
            const selectedUOM = unitOfMeasuresData.filter(uom => uom.UnitOfMeasure === unitOfMeasure);
            if (!selectedUOM.length) {
                setProductQty(possibleMinQty);
            }
        }

        // we need this in case the qtyOrdered is not equal to the minimumOrderQty (quick order only)
        if (isQuickOrder) {
            setProductQty(`${qtyOrdered || ""}`);
        }
    }, [qtyOrdered]);

    useEffect(() => {
        const selectedUOM = unitOfMeasuresData.filter(uom => uom.UnitOfMeasure === unitOfMeasure);
        let minQty;
        if (selectedUOM.length) {
            minQty = parseFloat(selectedUOM[0].MinimumOrderQuantity) || 1;
        } else {
            minQty = parseFloat(productProperties?.minimumOrderQty) || parseFloat(minimumOrderQty?.toString()) || 1;
        }

        const isFractionalMoq = !Number.isInteger(minQty);
        setAllowFractionalQty(isFractionalMoq);

        if (!selectedUOM.length) {
            const value =
                parseFloat(productProperties?.minimumOrderQty) || parseFloat(minimumOrderQty?.toString()) || 1;
            setProductQty(value || qtyOrdered);
        }
    }, [product?.productNumber]);

    useEffect(() => {
        if (isQuote) {
            setProductQty((product as any).qtyOrdered);
        }
    }, [product]);

    const disableDecreaseButton = useMemo(() => {
        if (isQuote) {
            if (!isProductFromCart) {
                if (Number(productQty) <= initialProductQty) {
                    return true;
                }
            } else if (Number(productQty) < originalQuotedQty - qtyCameFromCartMerge) {
                return true;
            }

            return false;
        }
        return false;
    }, [originalQuotedQty, qtyCameFromCartMerge, isProductFromCart, isQuote, productQty, initialProductQty]);

    // </xng-change>

    // Xng-Change - adding alwaysVisible to the condition
    if (!alwaysVisible && ((!hasEnoughInventory && !failedToLoadInventory) || !canAddToCart)) {
        return null;
    }

    const keyPressHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.which === 13) {
            (event.target as any).blur();
        }
    };

    // Xng-Change
    const qtyChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        if (!isValidNumberic(event.currentTarget.value)) {
            event.preventDefault();
            return;
        }
        const value = event.target.value;
        setProductQty(value);

        if (!onQtyOrderedChanged) {
            logger.warn(`There was no onQtyOrderedChanged passed to the ProductContext for ${productId}`);
            return;
        }

        const possibleNumber = Number(value);
        if (!isQuote && !Number.isNaN(possibleNumber)) {
            onQtyOrderedChanged(possibleNumber);
        }
    };

    // <xng-change>
    const qtyOnBlurHandler = (event: React.FocusEvent<HTMLInputElement>) => {
        if (!event) {
            return;
        }

        const value = parseFloat(event.currentTarget.value) || 0;
        const selectedUOM = unitOfMeasuresData?.find(uom => uom.UnitOfMeasure === unitOfMeasure);
        const minQty = selectedUOM
            ? parseFloat(selectedUOM.MinimumOrderQuantity) || 1
            : parseFloat(productProperties?.minimumOrderQty) || parseFloat(minimumOrderQty?.toString()) || 1;
        const msq = selectedUOM
            ? parseFloat(selectedUOM.MultipleSaleQuantity) || 1
            : parseFloat(productProperties?.multipleSaleQty) || 1;

        let newValue = getValidatedQty(value, minQty, msq);

        if (selectedUOM) {
            setProductQty(newValue.toString());
            onQtyOrderedChanged?.(newValue);
            return;
        }

        if (isQuote) {
            if (!isProductFromCart) {
                if (value <= initialProductQty) {
                    newValue = initialProductQty;
                }
            } else if (value < originalQuotedQty - qtyCameFromCartMerge) {
                newValue = initialProductQty;
            }
        } else if (possibleMinQty && value < parseFloat(possibleMinQty)) {
            newValue = parseFloat(possibleMinQty);
        }

        setProductQty(newValue.toString());
        onQtyOrderedChanged?.(newValue);
    };

    // compute current UOM-specific constraints to pass to QtyField so it reacts on UOM change
    const selectedUOMForProps = unitOfMeasuresData?.find(uom => uom.UnitOfMeasure === unitOfMeasure);
    const computedMinimumOrderQty = selectedUOMForProps
        ? parseFloat(selectedUOMForProps.MinimumOrderQuantity) || 1
        : parseFloat(productProperties?.minimumOrderQty) || parseFloat(minimumOrderQty?.toString()) || 1;
    const computedMultipleSaleQty = selectedUOMForProps
        ? parseFloat(selectedUOMForProps.MultipleSaleQuantity) || 1
        : parseFloat(productProperties?.multipleSaleQty) || 1;
    // </xng-change>

    return (
        // Xng-Change - using QtyField
        //  <TextField
        //     label={labelOverride ?? translate("QTY")}
        //     value={productQty}
        //     type="number"
        //     min={0}
        //     max={999999}
        //     data-test-selector="product_qtyOrdered"
        //     {...otherProps}
        //     onKeyPress={keyPressHandler}
        //     onChange={qtyChangeHandler}
        //     {...styles}
        // />
        <QtyField
            value={productQty}
            label={labelOverride ?? translate("QTY")}
            showLabel={!!labelOverride}
            // xng-change - EIS-4192 - adding aria-label for accessibility
            aria-label={translate("QTY")}
            maxLength={9}
            id={productId}
            onKeyPress={keyPressHandler}
            onChange={qtyChangeHandler}
            onBlur={qtyOnBlurHandler}
            disabled={alwaysVisible && (!productId || !hasEnoughInventory || !canAddToCart)}
            {...otherProps}
            allowFractionalQty={allowFractionalQty}
            multipleSaleQty={computedMultipleSaleQty}
            minimumOrderQty={computedMinimumOrderQty}
            isQuote={isQuote}
            disableDecreaseButton={disableDecreaseButton}
        />
    );
};

export default withProductContext(connect(mapStateToProps)(ProductQuantityOrdered));
